import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { Layout } from './components/Layout';
import './index.css';
import './styles/mobile-optimizations.css';

// ============================================
// LAZY LOADING: Публичные страницы
// ============================================
const App = lazy(() => import('./App'));
const Catalog = lazy(() => import('./pages/Catalog').then(m => ({ default: m.Catalog })));
const Cart = lazy(() => import('./pages/Cart').then(m => ({ default: m.Cart })));
const Checkout = lazy(() => import('./pages/Checkout').then(m => ({ default: m.Checkout })));
const Account = lazy(() => import('./pages/Account').then(m => ({ default: m.Account })));
const Favorites = lazy(() => import('./pages/Favorites').then(m => ({ default: m.Favorites })));
const Article = lazy(() => import('./pages/Article').then(m => ({ default: m.Article })));
const Articles = lazy(() => import('./pages/Articles').then(m => ({ default: m.Articles })));
const OrderDetails = lazy(() => import('./pages/OrderDetails').then(m => ({ default: m.OrderDetails })));
const GuestOrder = lazy(() => import('./pages/GuestOrder').then(m => ({ default: m.GuestOrder })));
const AddressManager = lazy(() => import('./pages/AddressManager').then(m => ({ default: m.AddressManager })));
const About = lazy(() => import('./pages/About').then(m => ({ default: m.About })));
const Marketplaces = lazy(() => import('./pages/Marketplaces').then(m => ({ default: m.Marketplaces })));
const Delivery = lazy(() => import('./pages/Delivery').then(m => ({ default: m.Delivery })));
const Payment = lazy(() => import('./pages/Payment').then(m => ({ default: m.Payment })));
const ProductDetails = lazy(() => import('./pages/ProductDetails').then(m => ({ default: m.ProductDetails })));
const Service = lazy(() => import('./pages/Service').then(m => ({ default: m.Service })));
const Privacy = lazy(() => import('./pages/Privacy').then(m => ({ default: m.Privacy })));
const Terms = lazy(() => import('./pages/Terms').then(m => ({ default: m.Terms })));
const Requisites = lazy(() => import('./pages/Requisites').then(m => ({ default: m.Requisites })));
const Callback = lazy(() => import('./pages/auth/Callback').then(m => ({ default: m.Callback })));

// ============================================
// LAZY LOADING: Админ-панель (отдельный чанк)
// ============================================
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout').then(m => ({ default: m.AdminLayout })));
const Dashboard = lazy(() => import('./pages/admin/Dashboard').then(m => ({ default: m.Dashboard })));
const ProductManager = lazy(() => import('./pages/admin/ProductManager').then(m => ({ default: m.ProductManager })));
const ProductEdit = lazy(() => import('./pages/admin/ProductEdit').then(m => ({ default: m.ProductEdit })));
const OrderManager = lazy(() => import('./pages/admin/OrderManager').then(m => ({ default: m.OrderManager })));
const CouponManager = lazy(() => import('./pages/admin/CouponManager').then(m => ({ default: m.CouponManager })));
const UserManager = lazy(() => import('./pages/admin/UserManager').then(m => ({ default: m.UserManager })));
const Analytics = lazy(() => import('./pages/admin/Analytics').then(m => ({ default: m.Analytics })));
const Archive = lazy(() => import('./pages/admin/Archive').then(m => ({ default: m.Archive })));
const Settings = lazy(() => import('./pages/admin/Settings').then(m => ({ default: m.Settings })));
const ArticleManager = lazy(() => import('./pages/admin/ArticleManager').then(m => ({ default: m.ArticleManager })));
const FeaturedProductsManager = lazy(() => import('./pages/admin/FeaturedProductsManager').then(m => ({ default: m.FeaturedProductsManager })));
const PriceManager = lazy(() => import('./pages/admin/PriceManager'));

// ============================================
// LOADING FALLBACK
// ============================================
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-[#FFCC00] border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-500">Загрузка...</p>
      </div>
    </div>
  );
}

// ============================================
// SERVICE WORKER
// ============================================
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(
      (registration) => {
        console.log('ServiceWorker registration successful');
      },
      (err) => {
        console.log('ServiceWorker registration failed: ', err);
      }
    );
  });
}

// ============================================
// ROUTER
// ============================================
const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageLoader />}>
            <App />
          </Suspense>
        ),
      },
      {
        path: 'catalog',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Catalog />
          </Suspense>
        ),
      },
      {
        path: 'shop',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Catalog />
          </Suspense>
        ),
      },
      {
        path: 'new',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Catalog defaultCondition="Новый" />
          </Suspense>
        ),
      },
      {
        path: 'discounted',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Catalog showDiscounted />
          </Suspense>
        ),
      },
      {
        path: 'sale',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Catalog showFeatured />
          </Suspense>
        ),
      },
      {
        path: 'cart',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Cart />
          </Suspense>
        ),
      },
      {
        path: 'checkout',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Checkout />
          </Suspense>
        ),
      },
      {
        path: 'account',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Account />
          </Suspense>
        ),
      },
      {
        path: 'favorites',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Favorites />
          </Suspense>
        ),
      },
      {
        path: 'articles',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Articles />
          </Suspense>
        ),
      },
      {
        path: 'article/:articleId',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Article />
          </Suspense>
        ),
      },
      {
        path: 'order/:orderId',
        element: (
          <Suspense fallback={<PageLoader />}>
            <OrderDetails />
          </Suspense>
        ),
      },
      {
        path: 'guest-order/:orderId',
        element: (
          <Suspense fallback={<PageLoader />}>
            <GuestOrder />
          </Suspense>
        ),
      },
      {
        path: 'addresses',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AddressManager />
          </Suspense>
        ),
      },
      {
        path: 'about',
        element: (
          <Suspense fallback={<PageLoader />}>
            <About />
          </Suspense>
        ),
      },
      {
        path: 'marketplaces',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Marketplaces />
          </Suspense>
        ),
      },
      {
        path: 'delivery',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Delivery />
          </Suspense>
        ),
      },
      {
        path: 'payment',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Payment />
          </Suspense>
        ),
      },
      {
        path: 'product/:id',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProductDetails />
          </Suspense>
        ),
      },
      {
        path: 'service',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Service />
          </Suspense>
        ),
      },
      {
        path: 'privacy',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Privacy />
          </Suspense>
        ),
      },
      {
        path: 'terms',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Terms />
          </Suspense>
        ),
      },
      {
        path: 'requisites',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Requisites />
          </Suspense>
        ),
      },
      {
        path: 'info',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Requisites />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '/auth/callback',
    element: (
      <Suspense fallback={<PageLoader />}>
        <Callback />
      </Suspense>
    ),
  },
  {
    path: '/admin',
    element: (
      <Suspense fallback={<PageLoader />}>
        <AdminLayout />
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageLoader />}>
            <Dashboard />
          </Suspense>
        ),
      },
      {
        path: 'products',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProductManager />
          </Suspense>
        ),
      },
      {
        path: 'products/new',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProductEdit />
          </Suspense>
        ),
      },
      {
        path: 'products/edit/:id',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProductEdit />
          </Suspense>
        ),
      },
      {
        path: 'orders',
        element: (
          <Suspense fallback={<PageLoader />}>
            <OrderManager />
          </Suspense>
        ),
      },
      {
        path: 'coupons',
        element: (
          <Suspense fallback={<PageLoader />}>
            <CouponManager />
          </Suspense>
        ),
      },
      {
        path: 'users',
        element: (
          <Suspense fallback={<PageLoader />}>
            <UserManager />
          </Suspense>
        ),
      },
      {
        path: 'analytics',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Analytics />
          </Suspense>
        ),
      },
      {
        path: 'archive',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Archive />
          </Suspense>
        ),
      },
      {
        path: 'articles',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ArticleManager />
          </Suspense>
        ),
      },
      {
        path: 'settings',
        element: (
          <Suspense fallback={<PageLoader />}>
            <Settings />
          </Suspense>
        ),
      },
      {
        path: 'featured-products',
        element: (
          <Suspense fallback={<PageLoader />}>
            <FeaturedProductsManager />
          </Suspense>
        ),
      },
      {
        path: 'price-manager',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PriceManager />
          </Suspense>
        ),
      },
    ],
  },
]);

// ============================================
// RENDER
// ============================================
const root = createRoot(document.getElementById('root')!);
root.render(
  <StrictMode>
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          <RouterProvider router={router} />
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
  </StrictMode>
);
